import { Badge, Card, Group, Stack, Text } from '@mantine/core';
import type { DealExtended } from '../DealViewController';
import type { DealFinancialSummary } from '@ayocore/erp/crm/server/repositories/DealRepository';

interface DealFinancialBlockProps {
  deal: DealExtended;
  financialSummary: DealFinancialSummary | null;
}

const fmt = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });

function formatAmount(value: number | null | undefined): string {
  return `${fmt.format(value ?? 0)} \u20BD`;
}

const cardStyle = { minWidth: 120, textAlign: 'right' as const, position: 'relative' as const };

function MarginChip({ percent }: { percent: number | null }) {
  if (percent == null || !isFinite(percent)) return null;
  return (
    <Badge
      size='xs'
      variant='filled'
      color={percent >= 30 ? 'teal' : 'red'}
      style={{ position: 'absolute', top: 4, left: 4, fontSize: 9, padding: '0 4px' }}
    >
      {percent}%
    </Badge>
  );
}

/**
 * Блок финансовой сводки сделки.
 *
 * Карточки: Сумма | Оплачено | Отгружено | Расходы | Прибыль | План.расходы | План.прибыль
 */
export function DealFinancialBlock({ deal, financialSummary }: DealFinancialBlockProps) {
  const rowsTotal = deal.rows_total != null ? Number(deal.rows_total) : null;
  const dealAmount = deal.amount != null ? Number(deal.amount) : null;
  const effectiveAmount = rowsTotal ?? dealAmount;
  const hasAmount = effectiveAmount != null && effectiveAmount > 0;

  const expensesFact = financialSummary?.expensesTotal ?? 0;
  const profitFact = hasAmount ? effectiveAmount - expensesFact : null;

  // % маржи приходит с сервера. Клиентский модуль может зарегистрировать свою
  // формулу через `DealRepositoryExtensions.marginPercentSql` — никаких локальных
  // вычислений и client-side override'ов здесь больше не нужно.
  const marginFact = financialSummary?.marginPercent ?? null;

  const plannedCost = financialSummary?.plannedCostTotal ?? 0;
  const plannedProfit = hasAmount ? effectiveAmount - plannedCost : null;
  // % плановой маржи приходит с сервера, уже округлённый до 2 знаков.
  const plannedMargin = financialSummary?.plannedMarginPercent ?? null;

  const expensesWarning = hasAmount
    ? expensesFact === 0 || expensesFact > effectiveAmount * 0.75
    : expensesFact === 0;

  return (
    <Group gap='xs' wrap='wrap'>
      <Card padding='xs' shadow='none' style={cardStyle}>
        <Stack gap={2} align='flex-end'>
          <Text size='xs' c='dimmed'>
            Сумма сделки
          </Text>
          <Text size='sm' fw={600}>
            {hasAmount ? `${fmt.format(effectiveAmount)} \u20BD` : '—'}
          </Text>
        </Stack>
      </Card>

      <Card padding='xs' shadow='none' style={cardStyle}>
        <Stack gap={2} align='flex-end'>
          <Text size='xs' c='dimmed'>
            Оплачено
          </Text>
          <Text size='sm' fw={600}>
            {formatAmount(financialSummary?.paidTotal)}
          </Text>
        </Stack>
      </Card>

      <Card padding='xs' shadow='none' style={cardStyle}>
        <Stack gap={2} align='flex-end'>
          <Text size='xs' c='dimmed'>
            Отгружено
          </Text>
          <Text size='sm' fw={600}>
            {formatAmount(financialSummary?.shippedTotal)}
          </Text>
        </Stack>
      </Card>

      <Card padding='xs' shadow='none' style={cardStyle}>
        <Stack gap={2} align='flex-end'>
          <Text size='xs' c='dimmed'>
            Расходы
          </Text>
          <Text size='sm' fw={600} c={expensesWarning ? 'red' : undefined}>
            {formatAmount(expensesFact)}
          </Text>
        </Stack>
      </Card>

      <Card padding='xs' shadow='none' style={cardStyle}>
        <MarginChip percent={marginFact} />
        <Stack gap={2} align='flex-end'>
          <Text size='xs' c='dimmed'>
            Прибыль
          </Text>
          <Text
            size='sm'
            fw={600}
            c={expensesFact === 0 || (profitFact != null && profitFact < 0) ? 'red' : 'teal'}
          >
            {profitFact != null ? formatAmount(profitFact) : '—'}
          </Text>
        </Stack>
      </Card>

      <Card padding='xs' shadow='none' style={cardStyle}>
        <Stack gap={2} align='flex-end'>
          <Text size='xs' c='dimmed'>
            План.расходы
          </Text>
          <Text size='sm' fw={600}>
            {formatAmount(plannedCost)}
          </Text>
        </Stack>
      </Card>

      <Card padding='xs' shadow='none' style={cardStyle}>
        <MarginChip percent={plannedMargin} />
        <Stack gap={2} align='flex-end'>
          <Text size='xs' c='dimmed'>
            План.прибыль
          </Text>
          <Text
            size='sm'
            fw={600}
            c={plannedCost === 0 || (plannedProfit != null && plannedProfit < 0) ? 'red' : 'teal'}
          >
            {plannedProfit != null ? formatAmount(plannedProfit) : '—'}
          </Text>
        </Stack>
      </Card>
    </Group>
  );
}
